/**
 * Last incoming crew message, over the map.
 *
 * Not a log. One line, then it fades. Own sends stay in the Chat tab.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { connectCrewChatStream, type CrewChatMessage } from '../../services/crewChatApi';
import { useAuthStore } from '../../stores/authStore';
import { decodeChatText, isCrewChatMessage } from './crewChat';

const CHANNEL = 'general';
const HOLD_MS = 5200;
const FADE_MS = 1400;

type Toast = { id: number; name: string; text: string };

const MissionChatToast: React.FC = () => {
  const email = useAuthStore((s) => s.user?.email?.toLowerCase() || '');
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    const es = connectCrewChatStream(CHANNEL, {
      onMessage: (incoming: CrewChatMessage) => {
        if (!isCrewChatMessage(incoming)) return;
        if (email && incoming.user_email.toLowerCase() === email) return;
        const body = decodeChatText(incoming.message || '').trim();
        const text = incoming.message_type === 'image'
          ? (body && body !== 'Image' ? body : 'sent a photo')
          : body;
        if (!text) return;
        setToast({
          id: incoming.id,
          name: incoming.user_name || incoming.user_email,
          text,
        });
      },
    });
    return () => es.close();
  }, [email]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), HOLD_MS + FADE_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!toast) return null;

  return (
    <Box
      key={toast.id}
      sx={{
        position: 'absolute',
        left: '50%',
        top: 72,
        zIndex: 1400,
        maxWidth: 'min(420px, calc(100% - 120px))',
        px: 1.5,
        py: 0.85,
        borderRadius: 2,
        bgcolor: 'rgba(12,16,22,0.62)',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
        backdropFilter: 'blur(12px)',
        pointerEvents: 'none',
        '@keyframes missionChatToast': {
          '0%': { opacity: 0, transform: 'translateX(-50%) translateY(-10px)' },
          '10%': { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
          '74%': { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
          '100%': { opacity: 0, transform: 'translateX(-50%) translateY(-4px)' },
        },
        animation: `missionChatToast ${HOLD_MS + FADE_MS}ms ease forwards`,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.58rem',
          fontWeight: 800,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: 'rgba(232,235,238,0.55)',
          mb: 0.15,
        }}
      >
        {toast.name}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.82rem',
          fontWeight: 600,
          color: '#f3f6f8',
          lineHeight: 1.35,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {toast.text}
      </Typography>
    </Box>
  );
};

export default MissionChatToast;
